import { useCallback, useMemo, useState } from "react";
import { useApi, useLiveData } from "../api/api";
import { WorkOrder, WorkOrderStatus } from "../api/types";
import {
  AlertBanner,
  PageTitle,
  Panel,
  btnDanger,
  btnPrimary,
  inputClass,
} from "../api/controlPlaneUi";

const BADGE: Record<WorkOrderStatus, { cls: string; label: string }> = {
  pending: { cls: "bg-[#262420] text-[#9D9585]", label: "Pending approval" },
  activating: { cls: "bg-[#C9A260]/10 text-[#C9A260]", label: "Activating" },
  active: { cls: "bg-emerald-950/40 text-emerald-400", label: "Active" },
  rejected: { cls: "bg-amber-950/40 text-amber-400", label: "Rejected" },
  failed: { cls: "bg-red-950/40 text-red-400", label: "Failed" },
};

const Badge: React.FC<{ status: WorkOrderStatus }> = ({ status }) => {
  const b = BADGE[status] ?? { cls: "bg-[#262420] text-[#9D9585]", label: status };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${b.cls}`}>
      {b.label}
    </span>
  );
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "—");
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

// Domain + target repo scope the requirements context the pipeline analyzes.
const ScopeChips: React.FC<{ order: WorkOrder }> = ({ order }) => {
  if (!order.domain && !order.targetRepo) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {order.domain && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-100">
          <span className="font-semibold uppercase tracking-wide text-[9px]">
            domain
          </span>
          {order.domain}
        </span>
      )}
      {order.targetRepo && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-sky-50 text-sky-700 border border-sky-100 font-mono">
          <span className="font-semibold uppercase tracking-wide text-[9px]">
            repo
          </span>
          {order.targetRepo}
        </span>
      )}
    </div>
  );
};

const WorkOrdersPage: React.FC = () => {
  const api = useApi();
  const fetchWorkOrders = useCallback(() => api.getWorkOrders(), [api]);
  const { data, error, loading, refresh } = useLiveData<WorkOrder[]>(
    fetchWorkOrders,
    [fetchWorkOrders]
  );

  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const orders = useMemo(() => data ?? [], [data]);
  const pending = orders.filter((o) => o.status === "pending");
  const decided = orders.filter((o) => o.status !== "pending");

  const noteFor = (id: string) => notes[id]?.trim() || undefined;

  const clearNote = (id: string) =>
    setNotes((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const approve = async (o: WorkOrder) => {
    setBusy(o.id);
    setActionError(null);
    try {
      await api.approveWorkOrder(o.id, { runPipeline: true, note: noteFor(o.id) });
      clearNote(o.id);
      refresh();
    } catch (e) {
      const msg = errMsg(e);
      const hint = msg.toLowerCase().includes("not signed in") ? " (sign-in required to approve)" : "";
      setActionError(`Approve failed: ${msg}${hint}`);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (o: WorkOrder) => {
    setBusy(o.id);
    setActionError(null);
    try {
      await api.rejectWorkOrder(o.id, noteFor(o.id));
      clearNote(o.id);
      refresh();
    } catch (e) {
      const msg = errMsg(e);
      const hint = msg.toLowerCase().includes("not signed in") ? " (sign-in required to reject)" : "";
      setActionError(`Reject failed: ${msg}${hint}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Cluster" name="work orders" />

      <p className="text-sm text-slate-500">
        Placed by the <span className="font-mono">brain</span> app via webhook when
        new requirements are created. Each order is <strong>held for human
        approval</strong> — approving activates a task/VM; rejecting closes it.
      </p>

      {error && (
        <AlertBanner variant="error">
          {error} — is the control plane running?
        </AlertBanner>
      )}
      {actionError && <AlertBanner variant="error">{actionError}</AlertBanner>}

      {loading ? (
        <Panel>
          <p className="text-sm text-slate-500 font-mono">Loading work orders…</p>
        </Panel>
      ) : (
        <>
          <Panel title={`Pending approval (${pending.length})`}>
            {pending.length === 0 ? (
              <p className="text-sm text-slate-400">Nothing awaiting approval.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {pending.map((o) => (
                  <li key={o.id} className="py-3 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#E5E0D5]">{o.title}</span>
                        <Badge status={o.status} />
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 font-mono">
                        {o.source} · {o.slug} · {when(o.createdAt)}
                        {o.sourceRef ? ` · ${o.sourceRef}` : ""}
                      </div>
                      <ScopeChips order={o} />
                      {o.requirement && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-3 whitespace-pre-wrap">
                          {o.requirement}
                        </p>
                      )}
                      <label className="block mt-2 max-w-xl">
                        <span className="text-[10px] font-semibold uppercase text-slate-500">
                          Reviewer note (optional)
                        </span>
                        <input
                          type="text"
                          value={notes[o.id] ?? ""}
                          onChange={(e) =>
                            setNotes((prev) => ({ ...prev, [o.id]: e.target.value }))
                          }
                          className={`${inputClass} mt-1`}
                          placeholder="Context for accepting or rejecting this brain request…"
                          disabled={busy === o.id}
                        />
                      </label>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        className={btnPrimary}
                        disabled={busy === o.id}
                        onClick={() => approve(o)}
                      >
                        {busy === o.id ? "Approving…" : "Approve & activate"}
                      </button>
                      <button
                        type="button"
                        className={btnDanger}
                        disabled={busy === o.id}
                        onClick={() => reject(o)}
                      >
                        Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={`History (${decided.length})`}>
            {decided.length === 0 ? (
              <p className="text-sm text-slate-400">No decided work orders yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {decided.map((o) => (
                  <li key={o.id} className="py-2.5 flex items-center gap-3 text-sm">
                    <Badge status={o.status} />
                    <span className="font-medium text-[#E5E0D5] truncate">{o.title}</span>
                    <span className="text-xs text-slate-400 font-mono truncate">
                      {o.slug}
                    </span>
                    <span className="ml-auto text-xs text-slate-500 text-right">
                      {o.decidedBy ? `by ${o.decidedBy} · ` : ""}
                      {when(o.decidedAt)}
                      {o.note ? ` · ${o.note}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
};

export default WorkOrdersPage;
